import React, { useState, useRef } from "react";
import CustomHeader from "../../components/header/index.jsx";
import url from "../../config.js";
import "./sessionlogs.scss";

// Session logs are served by intelog-be (the /process backend), not the
// controller — same base the cloud-account pages use.
const base = `${url.backendUrl}/api/v1/session-logs`;

const authHeaders = () => ({
  "Content-Type": "application/json",
  "x-access-token": localStorage.getItem("token") || "",
});

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

function SessionLogs() {
  // Step 1 — search
  const [username, setUsername] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { user, count, sessions: [] }

  // User picker (so the admin needn't know the exact username/email).
  const [userOpts, setUserOpts] = useState([]);   // [{id, username, email, name}]
  const [showMenu, setShowMenu] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const debounceRef = useRef(null);
  const blurRef = useRef(null);

  const fetchUsers = async (q) => {
    setLoadingUsers(true);
    try {
      const qs = new URLSearchParams({ limit: "50" });
      if (q && q.trim()) qs.set("q", q.trim());
      const res = await fetch(`${base}/users?${qs.toString()}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      setUserOpts(res.ok && Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      console.warn("[session-logs][fe] user list failed:", err.message);
      setUserOpts([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const onUserInput = (val) => {
    setUsername(val);
    setShowMenu(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUsers(val), 250);
  };

  const onUserFocus = () => {
    setShowMenu(true);
    if (!userOpts.length && !loadingUsers) fetchUsers(username);
  };

  // Delay hide so a click (mousedown) on a menu item registers first.
  const onUserBlur = () => {
    if (blurRef.current) clearTimeout(blurRef.current);
    blurRef.current = setTimeout(() => setShowMenu(false), 150);
  };

  const pickUser = (u) => {
    setUsername(u.email || u.username);
    setShowMenu(false);
    setUserOpts([]);
  };

  // Step 2 — detail
  const [active, setActive] = useState(null);   // the selected session row
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logs, setLogs] = useState(null);       // { sessionId, objectCount, records: [] }
  const [logError, setLogError] = useState("");

  const search = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setActive(null);
    setLogs(null);
    if (!username.trim()) { setError("Enter a username or email."); return; }
    setSearching(true);
    try {
      const qs = new URLSearchParams({ username: username.trim() });
      // Day-granularity range: whole 'from' day through the end of the 'to' day.
      if (from) qs.set("from", new Date(`${from}T00:00:00`).toISOString());
      if (to) qs.set("to", new Date(`${to}T23:59:59.999`).toISOString());
      console.log(`[session-logs][fe] search → GET ${base}/search?${qs.toString()}`);
      const res = await fetch(`${base}/search?${qs.toString()}`, { headers: authHeaders() });
      console.log(`[session-logs][fe] search ← HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Search failed (${res.status})`);
      console.log(`[session-logs][fe] found ${data.count} session(s) for`, data.user);
      setResult(data);
    } catch (err) {
      console.error('[session-logs][fe] search failed:', err);
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  const viewLogs = async (session) => {
    setActive(session);
    setLogs(null);
    setLogError("");
    setLoadingLogs(true);
    try {
      console.log(`[session-logs][fe] viewLogs → GET ${base}/${session.sessionId}`);
      const res = await fetch(`${base}/${encodeURIComponent(session.sessionId)}`, { headers: authHeaders() });
      console.log(`[session-logs][fe] viewLogs ← HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Could not load logs (${res.status})`);
      console.log(`[session-logs][fe] session=${session.sessionId} objects=${data.objectCount} records=${(data.records || []).length}`);
      setLogs(data);
    } catch (err) {
      console.error('[session-logs][fe] viewLogs failed:', err);
      setLogError(err.message);
    } finally {
      setLoadingLogs(false);
    }
  };

  const saveJson = (data, sessionId) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `session-logs-${sessionId}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const download = () => { if (logs) saveJson(logs, logs.sessionId); };

  // Per-row: fetch this session's logs and download them directly (no need to
  // open the detail view first).
  const [downloadingId, setDownloadingId] = useState("");
  const downloadSession = async (session) => {
    setDownloadingId(session.sessionId);
    try {
      console.log(`[session-logs][fe] downloadSession → GET ${base}/${session.sessionId}`);
      const res = await fetch(`${base}/${encodeURIComponent(session.sessionId)}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Could not load logs (${res.status})`);
      saveJson(data, session.sessionId);
    } catch (err) {
      console.error('[session-logs][fe] downloadSession failed:', err);
      setError(err.message);
    } finally {
      setDownloadingId("");
    }
  };

  return (
    <div className="root">
      <CustomHeader />
      <main className="content">
        <div className="sl-page">
          <h1 className="sl-title">Session Logs</h1>
          <p className="sl-sub">Retrieve a user's SmartHub session logs for debugging.</p>

          {/* Step 1 — search */}
          <section className="sl-card">
            <form className="sl-form" onSubmit={search}>
              <label className="sl-combo">
                Username or email
                <input
                  value={username}
                  onChange={(e) => onUserInput(e.target.value)}
                  onFocus={onUserFocus}
                  onBlur={onUserBlur}
                  autoComplete="off"
                  placeholder="type a name, username or email…"
                />
                {showMenu && (
                  <div className="sl-combo-menu">
                    {loadingUsers && <div className="sl-combo-hint">Searching…</div>}
                    {!loadingUsers && userOpts.length === 0 && <div className="sl-combo-hint">No matching users</div>}
                    {!loadingUsers && userOpts.map((u) => (
                      <div key={u.id} className="sl-combo-item" onMouseDown={() => pickUser(u)}>
                        <span className="sl-combo-name">{u.name}</span>
                        <span className="sl-combo-email">{u.email}</span>
                      </div>
                    ))}
                  </div>
                )}
              </label>
              <label>
                From
                <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <label>
                To
                <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
              </label>
              <button type="submit" disabled={searching}>{searching ? "Searching…" : "Search"}</button>
            </form>
            {error && <div className="sl-error">{error}</div>}
          </section>

          {/* Step 2 — results list */}
          {result && (
            <section className="sl-card">
              <h2 className="sl-h2">
                {result.count} session{result.count === 1 ? "" : "s"} for {result.user.username} ({result.user.email})
              </h2>
              {result.count === 0 ? (
                <div className="sl-empty">No sessions found in this window.</div>
              ) : (
                <table className="sl-table">
                  <thead>
                    <tr>
                      <th>Started</th><th>Project</th><th>Tech</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.sessions.map((s) => (
                      <tr key={s.sessionId} className={active && active.sessionId === s.sessionId ? "sl-row-active" : ""}>
                        <td>{fmt(s.startedAt || s.createdAt)}</td>
                        <td>{s.projectName || "—"}</td>
                        <td>{s.technology || "—"}</td>
                        <td className="sl-actions">
                          <button type="button" onClick={() => viewLogs(s)}>View logs</button>
                          <button type="button" onClick={() => downloadSession(s)} disabled={downloadingId === s.sessionId}>
                            {downloadingId === s.sessionId ? "Downloading…" : "Download JSON"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}

          {/* Step 3 — detail */}
          {active && (
            <section className="sl-card">
              <div className="sl-detail-head">
                <h2 className="sl-h2">Logs · <span className="sl-mono">{active.sessionId}</span></h2>
                {logs && <button type="button" className="sl-download" onClick={download}>Download JSON</button>}
              </div>
              {loadingLogs && <div className="sl-empty">Loading…</div>}
              {logError && <div className="sl-error">{logError}</div>}
              {logs && (
                <>
                  <div className="sl-meta">{logs.records.length} record(s) across {logs.objectCount} object(s). Records newer than the last flush may not appear yet.</div>
                  <table className="sl-table">
                    <thead>
                      <tr><th>Time</th><th>Level</th><th>Event</th><th>Agent/Kind</th><th>Message / summary</th></tr>
                    </thead>
                    <tbody>
                      {logs.records.map((r, i) => (
                        <tr key={i}>
                          <td className="sl-mono">{fmt(r.ts)}</td>
                          <td><span className={`sl-level sl-${r.level || "info"}`}>{r.level || "—"}</span></td>
                          <td>{r.event || "—"}</td>
                          <td>{r.agentKey || r.kind || "—"}</td>
                          <td className="sl-msg">{r.message || r.summary || (r.model ? `model ${r.model}` : "") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </section>
          )}
        </div>
      </main>
    </div>
  );
}

export default SessionLogs;
